import React, { useEffect, useState } from "react";
import { DollarSign, Info, Plus, Pencil, Trash2, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import StatusBadge from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const EMPTY = { employee_email: "", period_start: "", period_end: "", pay_date: "", gross: 0, taxes: 0, net: 0, hours: 0, status: "Pending" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Payroll() {
  const { toast } = useToast();
  const [configured, setConfigured] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    setLoading(true);
    api.get("/payroll").then((r) => { setConfigured(r.data.configured); setRows(r.data.rows || []); })
      .catch((err) => toast({ title: "Couldn't load payroll", description: err.response?.data?.detail || "Try again.", variant: "destructive" }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ employee_email: row.employee_email || "", period_start: row.period_start || "", period_end: row.period_end || "",
      pay_date: row.pay_date || "", gross: row.gross || 0, taxes: row.taxes || 0, net: row.net || 0, hours: row.hours || 0, status: row.status || "Pending" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const email = form.employee_email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: "Invalid email", description: "Enter a valid employee email.", variant: "destructive" });
      return;
    }
    if (!form.period_start || !form.period_end) {
      toast({ title: "Missing dates", description: "Period start and end are required.", variant: "destructive" });
      return;
    }
    if (form.period_start > form.period_end) {
      toast({ title: "Invalid period", description: "Period start must be on or before period end.", variant: "destructive" });
      return;
    }
    const nums = { gross: Number(form.gross), taxes: Number(form.taxes), net: Number(form.net), hours: Number(form.hours) };
    if (Object.values(nums).some((n) => Number.isNaN(n) || n < 0)) {
      toast({ title: "Invalid amounts", description: "Hours and amounts must be zero or positive numbers.", variant: "destructive" });
      return;
    }
    const payload = { ...form, employee_email: email, pay_date: form.pay_date || null, ...nums };
    try {
      if (editId) await api.put(`/payroll/${editId}`, payload);
      else await api.post("/payroll", payload);
      toast({ title: editId ? "Pay period updated" : "Pay period added" });
      setOpen(false);
      load();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/payroll/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast({ title: "Pay period deleted" });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const exportCsv = () => {
    if (!rows.length) {
      toast({ title: "Nothing to export", description: "Add a pay period first.", variant: "destructive" });
      return;
    }
    const cols = ["employee_email", "period_start", "period_end", "pay_date", "hours", "gross", "taxes", "net", "status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Payroll exported", description: `${rows.length} pay period(s) downloaded as CSV.` });
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100">
              <DollarSign className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Payroll</h2>
              <p className="text-sm text-slate-500">Administrator-only pay periods, earnings, and disbursements.</p>
            </div>
          </div>
          {configured && (
            <div className="flex items-center gap-2">
              <Button onClick={exportCsv} variant="outline" data-testid="payroll-export-btn" className="gap-1.5">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
              <Button onClick={openNew} data-testid="payroll-add-btn" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700">
                <Plus className="h-4 w-4" /> Add pay period
              </Button>
            </div>
          )}
        </div>
      </Card>

      {!configured ? (
        <Card>
          <div className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/60 p-4" data-testid="payroll-not-configured">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div>
              <div className="font-semibold text-slate-900">Payroll table not yet in Supabase</div>
              <p className="mt-1 text-sm text-slate-600">
                Run the one-time script <code className="rounded bg-slate-100 px-1">PAYROLL_SETUP.sql</code> in your Supabase
                SQL Editor to create the <code className="rounded bg-slate-100 px-1">payroll</code> table. This page will
                connect automatically once it exists.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4">Employee</th>
                    <th className="py-2 pr-4">Period</th>
                    <th className="py-2 pr-4">Pay date</th>
                    <th className="py-2 pr-4">Hours</th>
                    <th className="py-2 pr-4">Gross</th>
                    <th className="py-2 pr-4">Taxes</th>
                    <th className="py-2 pr-4">Net</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50" data-testid={`payroll-row-${r.id}`}>
                      <td className="py-3 pr-4 font-medium text-slate-800">{r.employee_email}</td>
                      <td className="py-3 pr-4 text-slate-600">{r.period_start} → {r.period_end}</td>
                      <td className="py-3 pr-4 text-slate-600">{r.pay_date || "—"}</td>
                      <td className="py-3 pr-4 text-slate-600">{r.hours}</td>
                      <td className="py-3 pr-4 text-slate-600">{money(r.gross)}</td>
                      <td className="py-3 pr-4 text-slate-600">{money(r.taxes)}</td>
                      <td className="py-3 pr-4 font-semibold text-slate-800">{money(r.net)}</td>
                      <td className="py-3 pr-4"><StatusBadge status={r.status} /></td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`payroll-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="outline" size="sm" onClick={() => remove(r)} data-testid={`payroll-delete-${r.id}`} className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (<tr><td colSpan={9} className="py-8 text-center text-slate-400">No pay periods yet. Add one to get started.</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="payroll-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit pay period" : "Add pay period"}</DialogTitle>
            <DialogDescription>Enter the employee, pay period dates, hours, and amounts.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Employee email</Label>
              <Input type="email" value={form.employee_email} onChange={(e) => set("employee_email", e.target.value)} data-testid="payroll-input-email" placeholder="agent@example.com" />
            </div>
            <div className="space-y-1.5"><Label>Period start</Label><Input type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="payroll-input-start" /></div>
            <div className="space-y-1.5"><Label>Period end</Label><Input type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} data-testid="payroll-input-end" /></div>
            <div className="space-y-1.5"><Label>Pay date</Label><Input type="date" value={form.pay_date} onChange={(e) => set("pay_date", e.target.value)} data-testid="payroll-input-paydate" /></div>
            <div className="space-y-1.5"><Label>Hours</Label><Input type="number" step="0.01" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="payroll-input-hours" /></div>
            <div className="space-y-1.5"><Label>Gross ($)</Label><Input type="number" step="0.01" value={form.gross} onChange={(e) => set("gross", e.target.value)} data-testid="payroll-input-gross" /></div>
            <div className="space-y-1.5"><Label>Taxes ($)</Label><Input type="number" step="0.01" value={form.taxes} onChange={(e) => set("taxes", e.target.value)} data-testid="payroll-input-taxes" /></div>
            <div className="space-y-1.5"><Label>Net ($)</Label><Input type="number" step="0.01" value={form.net} onChange={(e) => set("net", e.target.value)} data-testid="payroll-input-net" /></div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="payroll-input-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Processing">Processing</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payroll-save-btn" className="bg-violet-600 text-white hover:bg-violet-700">{editId ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
